import React from 'react';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

import type { RiskRatingValue } from '../types';
import { getRiskCellInfo } from '../utils';
import {
  StyledRiskRatingCell,
  StyledRiskRatingChevron,
  StyledRiskRatingChip,
  StyledRiskRatingChipLabel,
  StyledRiskRatingChipSeparator,
  StyledRiskRatingContent,
  StyledRiskRatingLine,
} from '../styles';

interface RiskRatingCellProps {
  rating: RiskRatingValue;
  matrixRows: number;
  matrixColumns: number;
  editable: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

export const RiskRatingCell: React.FC<RiskRatingCellProps> = ({
  rating,
  matrixRows,
  matrixColumns,
  editable,
  onClick,
}) => {
  const { likelihood, severity } = rating;

  if (likelihood !== null && severity !== null) {
    const { score, bg, color } = getRiskCellInfo(severity, likelihood, matrixRows, matrixColumns);
    return (
      <StyledRiskRatingCell
        as={editable ? 'button' : 'div'}
        type={editable ? 'button' : undefined}
        $disabled={!editable}
        onClick={editable ? onClick : undefined}
        aria-label={`Risk rating: likelihood ${likelihood} times severity ${severity} equals ${score}`}
      >
        {/* Two lines so the column can stay narrow enough to fit an A4 portrait page:
            likelihood × severity on top, = score below. The band colour rides on every
            chip; the score chip is bold as the headline value. */}
        <StyledRiskRatingContent>
          <StyledRiskRatingLine>
            <StyledRiskRatingChipLabel>L</StyledRiskRatingChipLabel>
            <StyledRiskRatingChip $bg={bg} $color={color}>
              {likelihood}
            </StyledRiskRatingChip>
            <StyledRiskRatingChipSeparator>×</StyledRiskRatingChipSeparator>
            <StyledRiskRatingChipLabel>S</StyledRiskRatingChipLabel>
            <StyledRiskRatingChip $bg={bg} $color={color}>
              {severity}
            </StyledRiskRatingChip>
          </StyledRiskRatingLine>
          <StyledRiskRatingLine>
            <StyledRiskRatingChipSeparator>=</StyledRiskRatingChipSeparator>
            <StyledRiskRatingChip $bg={bg} $color={color} $bold>
              {score}
            </StyledRiskRatingChip>
          </StyledRiskRatingLine>
        </StyledRiskRatingContent>
        {editable && (
          <StyledRiskRatingChevron>
            <ArrowDropDownIcon fontSize="small" />
          </StyledRiskRatingChevron>
        )}
      </StyledRiskRatingCell>
    );
  }

  return (
    <StyledRiskRatingCell
      as={editable ? 'button' : 'div'}
      type={editable ? 'button' : undefined}
      $disabled={!editable}
      onClick={editable ? onClick : undefined}
      aria-label={editable ? 'Set risk rating' : 'No risk rating'}
    >
      <StyledRiskRatingContent>{editable ? 'Set rating' : '—'}</StyledRiskRatingContent>
      {editable && (
        <StyledRiskRatingChevron>
          <ArrowDropDownIcon fontSize="small" />
        </StyledRiskRatingChevron>
      )}
    </StyledRiskRatingCell>
  );
};
